// =============================================================================
// ui/state/useJournalHidden.ts — read and write which Journal sections are hidden.
// -----------------------------------------------------------------------------
// Modelled on `usePnlUnit` deliberately, down to the refusal below: both write a
// single key into `app_flags`, which is ONE JSONB column shared by every surface
// in both clients — sixteen keys on Josh's row.
//
// 🚨 WHY IT REFUSES TO WRITE BEFORE SETTINGS HAVE LOADED. A save sends the whole
// blob. Writing from a screen that never read it would send a blob built from
// `{}` and delete the leak rules, the display unit, the journal layout and the
// custom widgets. Hiding a section is not worth that risk, so the control is
// inert rather than destructive while the read is pending or failed.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { usePreferencesOptional } from './preferences';
import {
  journalHiddenRows, readJournalHidden, toggleHidden, withJournalHidden,
  type HideableUnit, type HidePhase,
} from '../../selectors/journalHidden';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface JournalHiddenState {
  /** The stored keys. Empty while settings are loading. */
  hidden: readonly string[];
  /** The curated set PLUS any stored key it does not recognise. */
  rows: readonly HideableUnit[];
  /** False while the read is pending or failed — the control must go inert. */
  canWrite: boolean;
  busy: boolean;
  error: string | null;
  /** Show or hide one unit. Returns an error message, or null on success. */
  setVisible: (id: string, visible: boolean) => Promise<string | null>;
}

const NOT_READY = 'Your settings haven’t loaded, so sections can’t be hidden yet — '
  + 'saving now would overwrite the settings this screen never read. Reload and try again.';

export function useJournalHidden(phase?: HidePhase): JournalHiddenState {
  // ⚠ TOLERANT READ. See `usePreferencesOptional` — a reader this deep must not
  // oblige every isolated render of a Journal form to mount a provider. The
  // WRITE below still refuses without one.
  const p = usePreferencesOptional();
  const prefsPhase = p?.phase ?? 'loading';
  const prefs = p?.prefs ?? null;
  const update = p?.update;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canWrite = prefsPhase === 'ready' && prefs !== null;
  const hidden = useMemo(() => readJournalHidden(prefs?.appFlags), [prefs]);
  // ⚠ ROWS ARE PER-PHASE; THE STORED LIST IS NOT. JournalSection, PlanEditor and
  // RecapEditor call this with no phase because all they need is `hidden`, to
  // decide a CSS class. Only the drawer asks for rows, and it knows its phase.
  const rows = useMemo(
    () => (phase ? journalHiddenRows(hidden, phase) : []), [hidden, phase],
  );

  const setVisible = useCallback(
    async (id: string, visible: boolean): Promise<string | null> => {
      if (!canWrite || !prefs || !update) { setError(NOT_READY); return NOT_READY; }
      // ⚠ Read the blob HERE, not at render — see the header.
      const flags = withJournalHidden(
        prefs.appFlags, toggleHidden(readJournalHidden(prefs.appFlags), id, visible),
      );
      setBusy(true);
      setError(null);
      try {
        // ⚠ ONE KEY IN THE PATCH, as in `usePnlUnit`. A second would be a column
        // this control never read.
        await update({ appFlags: flags });
        return null;
      } catch (e) {
        const msg = reportFailure(
          'save journal section visibility', e, 'That could not be saved.',
        );
        setError(msg);
        return msg;
      } finally {
        setBusy(false);
      }
    },
    [canWrite, prefs, update],
  );

  return { hidden, rows, canWrite, busy, error, setVisible };
}
